import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';

import { CellClickedEvent, GridReadyEvent, RowSelectedEvent, RowSelectionOptions } from '@ag-grid-community/core';
import { ActivatedRoute } from '@angular/router';
import {
  ActiveStatus,
  ActiveStatusEnum,
  EOQType,
  Feature,
  Manufacturer,
  Product,
  ProductFeatureChoice,
  ProductType,
  ProductTypeAlt,
  ProductTypeFeatureMap,
  ProductTypeImage,
  ProductTypeTagMap,
} from 'app/model/entities/entity-model';
import { ManufacturerFeedStatusEnum } from 'app/model/entities/enums';
import { FeatureKindEnum } from 'app/model/entities/-feature-kind-enum';
import { StagedFeedApiService } from '@/admin/staged-feed-api.service';
import { FeedSizeCoverageDTO, ProductTypeFeedHealthDTO, UnmatchedUniformaxColorDTO } from '@/admin/staged-feed.models';
import { RepointColorDialogComponent } from '@/shared/repoint-color-dialog.component';
import { SizeMappingDialogComponent } from '@/shared/size-mapping-dialog.component';
import { AuthService } from 'app/shared/auth.service';
import { FileService } from 'app/shared/file.service';
import _ from 'lodash';
import { takeUntil } from 'rxjs/operators';
import { DomainBaseComponent } from '../shared/domain-base.component';
import { DomainService } from '../shared/domain.service';

import { EOQTypeCode } from 'app/model/enums/eoq-type-code';
import { AgFns, GridOptionsExt, ISortModel, RowSelectionMultipleCheckbox, RowSelectionSingle } from 'app/shared/ag-fns';
import { DialogBaseComponent } from 'app/shared/dialog-base.component';
import { EntityFinderDialog } from 'app/shared/entity-finder.dialog';
import { EntityFns } from 'app/shared/entity-fns';
import { ImageHandlerComponent } from 'app/shared/image/image-handler.component';
import { ProductExposurePanelComponent } from '@/shared/product-exposure-panel.component';
import { ProductExposureApiService, ProductExposureFns, ProductExposureSummaryDTO } from '@/shared/product-exposure-api.service';
import { UtilFns } from 'app/shared/util-fns';
import { UniAgFns } from 'app/shared/uni-ag-fns';
import { DialogService } from 'primeng/dynamicdialog';
import { EditProductDialogComponent } from './edit-product-dialog.component';
import { FeatureChoiceFinderDialogComponent } from './feature-choice-finder-dialog.component';
import { FeatureFinderDialogComponent } from './feature-finder-dialog.component';
import { ManufacturerFinderDialogComponent } from './manufacturer-finder-dialog.component';

import { AddFeedColorsDialogComponent } from './add-feed-colors-dialog.component';
import { ProductBuilderDialogComponent } from './product-builder-dialog.component';
import { ProductTypeChangeFeaturesDialogComponent } from './product-type-change-features-dialog.component';
import { ProductTypeFinderDialogComponent } from './product-type-finder-dialog.component';
import { ProductReplacementDialogComponent } from './product-replacement-dialog.component';

/** The fixed suffix of the one style rename the gatekeeper allows. Same as the server's OldStyleSuffix. */
const OLD_STYLE_SUFFIX = '-OLD';

@Component({
  selector: 'app-product-type',
  templateUrl: './product-type.component.html',

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ProductTypeComponent extends DomainBaseComponent implements OnInit {
  @ViewChild(ImageHandlerComponent) imageHandler?: ImageHandlerComponent;
  @ViewChild('firstInput') firstInput: ElementRef<HTMLInputElement>;
  @ViewChild(ProductExposurePanelComponent) exposurePanel?: ProductExposurePanelComponent;

  productType: ProductType;
  isAdding: boolean;
  canEditRootStyleInfo: boolean;

  title: string;
  allManufs: Manufacturer[];
  searchManufs: Manufacturer[];

  features: Feature[];
  selectedFeature?: Feature;

  activeStatuses: ActiveStatus[];

  eOQTypes: EOQType[];

  productGridOptions: GridOptionsExt;
  allProducts: Product[] = [];
  products: Product[] = [];
  allowCombination: boolean;
  shouldShowInactiveProducts: boolean = false;
  selectedProduct?: Product;

  altItsGridOptions: GridOptionsExt;
  altProductTypes: ProductType[] = [];

  /** How this style stands against the manufacturer feed. Null when the manufacturer has none. */
  feedHealth: ProductTypeFeedHealthDTO | null = null;
  isFeedHealthOpen = false;
  isUpdatingDescription = false;

  /** The feed is being asked about the SKU right now. */
  isCheckingStyle = false;
  /** The manufacturer does not publish the SKU that was typed. */
  styleCheckError: string | null = null;

  /**
   * The product type this SKU collides with, when it collides with one.
   *
   * Held separately from styleCheckError because a duplicate is not the same kind of answer as
   * 'the manufacturer does not publish this'. The second is a judgement worth overriding; the
   * first means every other field on the form is about to be wasted work.
   */
  duplicateOf: ProductType | null = null;
  /** The feed could not be reached, so the SKU is unchecked rather than refused. */
  styleCheckWarning: string | null = null;
  /** manufacturer|style the feed has already answered for, so an unchanged blur asks nothing. */
  private checkedStyleKey: string | null = null;

  /** Exposed so the template can name the states rather than compare against 2 and 3. */
  ManufacturerFeedStatusEnum = ManufacturerFeedStatusEnum;

  rowSelection: RowSelectionOptions = RowSelectionSingle;

  // Migration code removed.
  // migrationProductTypeChange: _MigrationProductTypeChange;

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    protected authService: AuthService,
    public pngDialogService: DialogService,
    private http: HttpClient,
    private fileService: FileService,
    private stagedFeedApi: StagedFeedApiService,
    private productExposureApi: ProductExposureApiService,
  ) {
    super(domainService);

    this.route.paramMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  async canDeactivate() {
    var ok = await super.canDeactivate();
    if (!ok) return false;
    if (this.uow.hasChanges()) {
      const ok = await this.dialogService.yesNoDialog('Changes have not been saved', 'Do you want to save before exiting?');
      if (ok) {
        const ok = await this.saveChanges();
        if (!ok) {
          return false;
        }
      } else {
        this.dbSaveService.rejectChanges();
      }
    }

    // need to cleanup any pending changes.
    this.uow.manager.rejectChanges();

    this.uow.clearEntities(Manufacturer);
    this.uow.clearEntities(ProductTypeAlt);
    this.uow.clearEntities(Product);
    this.uow.clearEntities(ProductTypeImage);
    return true;
  }

  async updateFromContext() {
    const productTypeId = this.route.snapshot.params['productTypeId'];
    // The previous style's answer must not lock this one while the panel counts again.
    this.productUse = null;
    this.isAdding = productTypeId == 'add';
    if (this.isAdding) {
      this.productType = this.createNewProductTypeAsUnchanged();
    } else {
      this.productType = await this.dbQueryService.getProductType(productTypeId);
      // migration code removed
      // this.migrationProductTypeChange = await this.dbQueryService.getMigrationProductTypeChange(this.productType.id);
    }

    this.allProducts = this.productType.products.slice();
    this.applyProductFilter();
    this.altProductTypes = await this.getAltProductTypes(this.productType);

    const user = this.authService.getUser();
    this.canEditRootStyleInfo = user.isProductAdmin || this.isAdding;
    this.title = 'References - Product Types (' + (this.isAdding ? 'Add Product Type' : 'Edit Product Type') + ')';
    const allManufs = await this.uow.queryAll(Manufacturer, 'Manufacturers');
    this.allManufs = _.sortBy(allManufs, (m) => m.name);

    this.features = await this.dbQueryService.getFeatures(true);

    this.eOQTypes = this.dbQueryService.getAllCached(EOQType);
    // Hold has no purpose on a style (Jeff 2026-11-01), so it is not offered, the same as on Edit
    // Product. A style that is on Hold still shows it, so its status does not show as blank.
    this.activeStatuses = this.dbQueryService.getAllCached(ActiveStatus).filter(
      (s) => s.id !== ActiveStatusEnum.Hold || this.productType?.activeStatusId === ActiveStatusEnum.Hold,
    );

    this.productGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onProductGridReady,
      onRowSelected: this.onProductRowSelected,
      getRowId: (x) => {
        return x.data.id.toString();
      },
    });

    this.altItsGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onAltItsGridReady,
    });

    await this.refreshFeedHealth();
    await this.refreshConversion();

    this.isPageReady = true;

    // Focus the first input after the view renders
    await UtilFns.wait(1);
    this.firstInput?.nativeElement?.focus();
  }

  private createNewProductTypeAsUnchanged() {
    const productType = this.uow.createEntity(ProductType, {
      modUserInit: this.authService.getUser().initials,
      eOQTypeId: EOQTypeCode.None,
      manufacturerId: null,
      // A style is being added because somebody intends to sell it. Leaving Status blank made
      // that an extra field to remember, and a blank one reads as a decision nobody made.
      activeStatusId: ActiveStatusEnum.Active,
    });
    productType.entityAspect.setUnchanged();
    productType.entityAspect.validateEntity();
    return productType;
  }

  onDisplayOrderChanged(ptfm: ProductTypeFeatureMap, e: string) {
    ptfm.displayOrder = +e;
    AgFns.refreshGrid(this.productGridOptions, this.products);
  }

  /**
   * How this style stands against the manufacturer feed. Left null when the manufacturer has
   * no feed, which is what keeps the banner and health panel off every other manufacturer.
   */
  async refreshFeedHealth() {
    this.feedHealth = null;

    // Add asks by style: there is no id yet. Every Edit asks by id and sends what is on screen -
    // the features, and the SKU and manufacturer when they are unsaved - so the panel describes the
    // screen while the shape and the inactive flag are still judged. Asking by style on an Edit
    // whose SKU had been touched dropped both, and BG7224 with no Color read "Aligned".
    const health = this.isAdding
      ? await this.readStyleHealth()
      : this.productType?.id == null
        ? null
        : await this.stagedFeedApi.getProductTypeHealth(this.productType.id, {
            sizeFeatureId: this.attachedSizeFeatureId,
            featureIds: this.attachedFeatureIds,
            style: this.isStyleUnsaved && !this.isOldStyleRename ? this.productType.style?.trim() : null,
            manufacturerId: this.isStyleUnsaved && !this.isOldStyleRename ? this.productType.manufacturerId : null,
          });

    if (health == null || health.ManufacturerFeedStatusId === ManufacturerFeedStatusEnum.None) return;
    this.feedHealth = health;

    // The feed has already answered for this style, so record that leaving the SKU field need not
    // ask again.
    //
    // Without this, checkedStyleKey stays null until somebody edits the SKU - and on an Edit page
    // the SKU holds focus from load, so the FIRST CLICK ANYWHERE blurs it and runs the full check:
    // the panel is nulled, every control that reads it is destroyed mid-click, and a live call goes
    // to the manufacturer to re-read a style that was read already. The click lands on a button
    // that no longer exists, which is why anything on this page took two clicks.
    if (health.IsStyleStaged && !this.isStyleUnsaved) {
      const manufacturerId = this.productType?.manufacturerId;
      const style = this.productType?.style?.trim();
      if (manufacturerId != null && style) {
        this.checkedStyleKey = this.styleKey(manufacturerId, style);
      }
    }
  }

  private async readStyleHealth(): Promise<ProductTypeFeedHealthDTO | null> {
    const manufacturerId = this.productType?.manufacturerId;
    const style = this.productType?.style?.trim();
    if (manufacturerId == null || !style) return null;
    return await this.stagedFeedApi.getStyleHealth(manufacturerId, style, {
      sizeFeatureId: this.attachedSizeFeatureId,
      featureIds: this.attachedFeatureIds,
    });
  }

  /**
   * The size scale this style is being built on, as the screen currently has it.
   *
   * Sent to the server rather than letting it look the feature up, because on Add there is no
   * saved ProductType to look it up from - and the sweep that fills the staged size pointers only
   * considers features of SAVED product types, so a new style has none of them. Without this the
   * panel would call every published size unbuildable on exactly the screen where it matters most.
   */
  private get attachedSizeFeatureId(): number | null {
    const kindByFeatureId = new Map((this.features ?? []).map((f) => [f.id, f.featureKindId]));
    const map = (this.productType?.productTypeFeatureMaps ?? [])
      .filter((m) => !m.entityAspect.entityState.isDeleted() && !m.entityAspect.entityState.isDetached())
      .find((m) => kindByFeatureId.get(m.featureId) === FeatureKindEnum.Size);
    return map?.featureId ?? null;
  }

  /** Every feature attached on the screen, saved or not. */
  private get attachedFeatureIds(): number[] {
    return (this.productType?.productTypeFeatureMaps ?? [])
      .filter((m) => !m.entityAspect.entityState.isDeleted() && !m.entityAspect.entityState.isDetached())
      .map((m) => m.featureId);
  }

  /** Whether the style on the screen is one the server has not been told about yet. */
  private get isStyleUnsaved() {
    const original = this.productType?.entityAspect?.originalValues ?? {};
    return this.isAdding || 'style' in original || 'manufacturerId' in original;
  }

  /**
   * The one style rename the gatekeeper allows: a saved style renamed to its SKU plus -OLD, so a feed
   * style can be made with the name (K500 -> K500-OLD). The feed does not have the -OLD name, so the
   * SKU checks do not ask it. Same rule as the server's IsOldStyleRename (Jeff 2026-11-01, CTB151).
   */
  private get isOldStyleRename() {
    if (this.isAdding || !this.productType) return false;
    const original = this.productType.entityAspect?.originalValues ?? {};
    if (!('style' in original) || 'manufacturerId' in original) return false;
    const from = String((original as any).style ?? '').trim().toUpperCase();
    const to = (this.productType.style ?? '').trim().toUpperCase();
    return from.length > 0 && !from.endsWith(OLD_STYLE_SUFFIX) && to === from + OLD_STYLE_SUFFIX;
  }

  /**
   * Ask the manufacturer about the SKU as soon as the field is left, rather than at save.
   *
   * Everything the form offers afterwards - the description, the colors, the health panel - is
   * only meaningful for a style the manufacturer publishes, so that answer has to arrive before
   * any of it is filled in. Learning it at save is a form filled in twice.
   *
   * Only for a manufacturer with a feed. There is nobody to ask about anyone else's SKU.
   */
  async onStyleBlur() {
    // Upper case, not merely trimmed. Manufacturers publish style codes in capitals and look
    // them up without regard to case, so 'dt6700' passes every check on this form and is then
    // saved as a string the manufacturer does not use - invisible until something case-sensitive
    // meets it. Asking the feed for its own spelling would be better, and SanMar does not state
    // one in its response, so the field settles it here.
    const style = this.productType.style?.trim()?.toUpperCase();
    if (style !== this.productType.style) this.productType.style = style;

    const mfr = this.feedManufacturer;
    if (mfr == null || !style) {
      this.clearStyleCheck();
      if (this.isStyleUnsaved) await this.refreshFeedHealth();
      return;
    }

    // A saved style whose SKU and manufacturer are unchanged asks nothing: the answer cannot change
    // anything, and Save waits on it. The load sets checkedStyleKey only for a style the feed has
    // read, so a custom style (CT10NV-UNITEC OWNED) asked Red Kap's whole weekly file on every
    // blur, with Save disabled meanwhile. The save path already asks only when the style moved.
    if (!this.isStyleUnsaved) return;

    // Leaving the field without changing it asks nothing. This is a live call to the
    // manufacturer, so tabbing back through the form must not repeat it.
    const key = this.styleKey(mfr.id, style);
    if (key === this.checkedStyleKey) return;

    // DO WE ALREADY CARRY IT? Asked first, and asked here rather than only at save.
    //
    // This is a local lookup and it is the cheapest question on the form, but it used to be
    // asked only once the whole Add form was filled in and Save was pressed - so the way to
    // find out a style already existed was to do all the work and then be refused. Asking on
    // the way out of the field says it while the cursor is still next to the mistake.
    //
    // Before the feed call on purpose: there is no reason to ask the manufacturer about a style
    // we cannot create anyway.
    const duplicate = await this.findDuplicateProductType();
    if (duplicate) {
      this.checkedStyleKey = null;
      this.feedHealth = null;
      this.styleCheckWarning = null;
      this.duplicateOf = duplicate;
      this.styleCheckError =
        `This manufacturer already has style '${duplicate.style}'` +
        ` (${duplicate.description || 'no description'}).` +
        ` Enter a different SKU, or edit the existing product type.`;
      return;
    }

    if (this.isOldStyleRename) {
      this.styleCheckError = null;
      this.styleCheckWarning = null;
      this.duplicateOf = null;
      this.checkedStyleKey = key;
      return;
    }

    this.isCheckingStyle = true;
    this.styleCheckError = null;
    this.styleCheckWarning = null;
    this.duplicateOf = null;
    this.feedHealth = null;
    try {
      const run = await this.stagedFeedApi.stageStyle(mfr.id, style);
      if (run.StylesFailed > 0) {
        this.checkedStyleKey = null;
        if (this.isStyleRefusal(run.Error) && this.isAdding) {
          // Revision 2: a style the feed does not have is a custom style, not a mistake. Save asks.
          this.styleCheckWarning =
            `The ${mfr.name} feed does not have style '${style}'.` +
            ` If you save, the system saves it as a custom style. A custom style does not use the feed.`;
        } else if (this.isStyleRefusal(run.Error)) {
          this.styleCheckError =
            `${mfr.name} does not publish style '${style}'.` + ` Check the SKU against the manufacturer catalog, then type it again.`;
        } else {
          this.styleCheckWarning =
            `The system could not check the SKU with ${mfr.name}.` +
            (run.Error ? ` ${run.Error}` : '') +
            ` The system checks the SKU again when you save.`;
        }
        return;
      }

      this.checkedStyleKey = key;
      await this.refreshFeedHealth();
      this.takeFeedDescription();
    } catch {
      // An outage at the manufacturer must not stop somebody building the catalogue. The save
      // path asks again and offers to continue, so this only reports that the check did not run.
      this.checkedStyleKey = null;
      this.styleCheckWarning =
        `The system cannot reach ${mfr.name} now, so it did not check the SKU.` + ` The system checks the SKU again when you save.`;
    } finally {
      this.isCheckingStyle = false;
    }
  }

  /**
   * Whether the feed refused the style itself, rather than the read failing.
   *
   * A refusal and a failure are not the same answer, and until now both read as 'does not
   * publish'. LK864 reported a fault in our own staging code as a verdict on the SKU, which
   * sends somebody off to correct a style that may be perfectly good.
   *
   * The manufacturers state a refusal in their own words - SanMar says 'Product Id not found' -
   * so this reads the text. Anything it does not recognise counts as a failed read, which is the
   * safe way round: an unchecked SKU is still checked again at save.
   */
  private isStyleRefusal(error: string | null) {
    return (error ?? '').toLowerCase().includes('not found');
  }

  private clearStyleCheck() {
    this.checkedStyleKey = null;
    this.styleCheckError = null;
    this.styleCheckWarning = null;
    this.duplicateOf = null;
  }

  private styleKey(manufacturerId: number, style: string) {
    return `${manufacturerId}|${style.toLowerCase()}`;
  }

  /**
   * Take the wording the manufacturer publishes for this style.
   *
   * The description is TAKEN rather than offered. On a feed manufacturer the published wording is
   * the right answer by definition, and making somebody retype it invites the drift that Update
   * Description exists to correct. Anything already typed is left alone - a person who has
   * written their own description meant it.
   */
  private takeFeedDescription() {
    const h = this.feedHealth;
    if (h == null || !h.IsStyleStaged) return;

    // Description is deliberately NOT touched. It is our catalogue label, written in a house
    // style - 'T-Shirt: Men's L/S 50/50' - and 605 of 607 SanMar styles carry our wording rather
    // than the manufacturer's. What the manufacturer calls it is shown read-only in the Feed
    // Health panel, from h.FeedDescription.
    if (!this.productType.descriptionExt && h.FeedDescriptionExt) {
      this.productType.descriptionExt = h.FeedDescriptionExt;
    }
  }

  get isFeedManaged() {
    return this.feedHealth != null;
  }

  /**
   * Whether the manufacturer, rather than Uniformax, owns this style's images.
   *
   * Keyed on feedManufacturer and not on feedHealth: health needs a style the feed has already
   * answered for, and the upload has to be gone from the moment the manufacturer is chosen -
   * before a SKU is typed. Anything uploaded in that gap is contradicted by the next read.
   * A saved custom style owns its images: the feed never reads it.
   */
  get areImagesFeedOwned() {
    return this.feedManufacturer != null;
  }

  /**
   * The manufacturer of this style when it has a feed and the style uses it. Null otherwise.
   *
   * feedHealth cannot answer this - it is computed from a style the feed has returned, and the
   * form has to say a check is coming BEFORE a SKU is typed, rather than refusing one afterwards
   * with no warning that it would.
   */
  get feedManufacturer(): Manufacturer | null {
    const mfr = this.productType?.manufacturer;
    if (mfr == null || this.isCustomStyle) return null;
    return mfr.manufacturerFeedStatusId === ManufacturerFeedStatusEnum.None ? null : mfr;
  }

  /**
   * A saved style that does not use the feed. The server decides this once, when the style is
   * created, and it never changes (gatekeeper F2). The feed checks and the feed images do not apply.
   */
  get isCustomStyle(): boolean {
    return !this.isAdding && (this.productType?.id ?? 0) > 0 && !this.productType.isFeedEnabled;
  }

  /**
   * Nothing on this form can be answered before the manufacturer is known: the SKU is checked
   * against it, the description comes from it, and which features the style needs depends on it.
   * So Add starts with every field locked and Select as the only thing to press.
   */
  get isFormLocked() {
    return (this.isAdding && this.productType?.manufacturer == null) || this.isStyleDuplicate;
  }

  /** Whether this SKU is one the manufacturer already has under another product type. */
  get isStyleDuplicate() {
    return this.duplicateOf != null;
  }

  /** Whether this user can edit the ordinary fields of this style right now. */
  /**
   * Gatekeeper P3.6, from the Product Use panel (null until it answers, and on a new style). The
   * feature brake is always on, so Add is off whenever a product was used. Change and the SKU are
   * meaning changes under the gate, so they are off only when the gate enforces. The click checks
   * and the server stay the authority, for an unknown (Proximity not checked) too.
   */
  productUse: ProductExposureSummaryDTO | null = null;

  get isFeatureBrakeLocked() {
    return !this.isAdding && (this.productUse?.UsedProducts ?? 0) > 0;
  }

  get isMeaningLocked() {
    return !this.isAdding && ProductExposureFns.isMeaningLocked(this.productUse);
  }

  /**
   * Gatekeeper FL, the feed style lock (Jeff 2026-11-01): on a saved feed style only new products,
   * Phaseout, Inactive and cosmetic edits are allowed. The server judges every save under the gate
   * mode; the controls go off only while the gate enforces, so Report mode shows what it would refuse.
   */
  get isFeedLocked() {
    return !this.isAdding && !!this.productType?.isFeedEnabled && (this.productType?.id ?? 0) > 0 && !!this.productUse?.MeaningGateEnforced;
  }

  /**
   * A saved feed style's features come from the feed and never change (Jeff 2026-11-02): Add and
   * Change are off always, whatever the gate mode. A new style chooses its features while it is added.
   */
  get isFeedFeatureLocked() {
    return !this.isAdding && !!this.productType?.isFeedEnabled && (this.productType?.id ?? 0) > 0;
  }

  get canEdit() {
    return this.user.isProductAdmin && !this.isFormLocked;
  }

  /**
   * The SKU field itself, which must stay editable precisely when the rest of the form does not.
   *
   * A duplicate SKU locks the form, and the SKU is the thing that has to change to unlock it.
   * Routing this through canEdit would disable the only field that can fix the problem.
   */
  get canEditStyle() {
    return this.user.isProductAdmin && this.canEditRootStyleInfo && this.productType?.manufacturer != null;
  }

  /**
   * Whether the manufacturer's wording may be taken for this style.
   *
   * Closed while the style is being added. The button saves, and on an Add page that save writes
   * the whole half-filled product type - the operator pressed Update Extended Description and got
   * a style they had not finished. The feed's wording is already in the field by then anyway:
   * takeFeedDescription fills it as soon as the SKU is answered for.
   */
  get canUpdateDescriptionFromFeed(): boolean {
    if (this.isAdding || !this.canEdit || this.isUpdatingDescription) return false;
    return !!this.feedHealth?.DescriptionDiffers;
  }

  /**
   * Take the manufacturer's own wording for this style. Description and Extended Description
   * are the two fields the feed supplies, and both already flow on to Proximity through the
   * ordinary ProductType push, so this needs no special delivery.
   */
  async onUpdateDescriptionFromFeed() {
    if (!this.canUpdateDescriptionFromFeed) return;

    const h = this.feedHealth;
    if (h == null || !h.IsStyleStaged) return;
    if (!(await this.isPageSaved())) return;

    const ok = await this.dialogService.yesNoDialog(
      'Update Extended Description',
      `The system replaces the extended description with the wording from the manufacturer.` +
        ` The description stays as it is.` +
        `<br/><br/><b>Extended Description</b><br/>${h.FeedDescriptionExt ?? '(empty)'}` +
        `<br/><br/>Do you want to continue?`,
      'Ok',
      'Cancel',
    );
    if (!ok) return;

    this.isUpdatingDescription = true;
    try {
      this.productType.descriptionExt = h.FeedDescriptionExt;
      const saved = await this.dbSaveService.saveChanges();
      if (saved) {
        this.toastr.success('The system updated the extended description from the manufacturer.');
        await this.refreshFeedHealth();
      }
    } finally {
      this.isUpdatingDescription = false;
    }
  }

  /**
   * Move every product of this style off a color the manufacturer does not make, onto one it
   * does. Same dialog the Manufacturer Feed page uses, so the warning about the change
   * reaching Proximity is stated once, in one place.
   */
  async onReassignColor(row: UnmatchedUniformaxColorDTO) {
    const h = this.feedHealth;
    if (h == null || h.UniformaxProductTypeIds.length === 0) return;
    if (!(await this.isPageSaved())) return;

    const result = await DialogBaseComponent.open(RepointColorDialogComponent, this, {
      productTypeIds: h.UniformaxProductTypeIds,
      styleCode: h.Style,
      fromFeatureChoiceId: row.FeatureChoiceId,
      fromColorName: row.UniformaxColorName,
      stagedColors: (await this.stagedFeedApi.getStyleColorsForProductType(this.productType.id)) ?? [],
    });
    if (result) {
      this.toastr.success(`The system changed ${result.changed} product(s) to '${result.toName}'.`);
      this.productType = await this.dbQueryService.getProductType(this.productType.id);
      this.allProducts = this.productType.products.slice();
      this.applyProductFilter();
      AgFns.refreshGrid(this.productGridOptions, this.products);
      await this.refreshFeedHealth();
    }
  }

  /** Add colors the manufacturer publishes to the Color list. */
  async onAddFeedColors() {
    const h = this.feedHealth;
    if (h == null || h.FeedColorsMissingFromOurList.length === 0) return;
    if (!(await this.isPageSaved())) return;

    const result = await DialogBaseComponent.open(AddFeedColorsDialogComponent, this, {
      colorNames: h.FeedColorsMissingFromOurList,
      styleCode: h.Style,
      manufacturerName: h.ManufacturerName ?? '',
    });
    if (result) {
      this.toastr.success(`The system added ${result.added} color(s) to the color list.`);
      this.features = await this.dbQueryService.getFeatures(true);
      await this.refreshFeedHealth();
    }
  }

  /**
   * Say what the manufacturer's sizes mean in Uniformax.
   *
   * The same screen the Manufacturer Feed page uses, and deliberately not filtered to this style.
   * An answer is recorded against the size feature, not the style, so answering 2XL once answers
   * it everywhere that feature is used - narrowing the list would hide that.
   */
  async onMatchSizes() {
    const h = this.feedHealth;
    if (h == null) return;

    // The panel has just printed which sizes are outstanding and which list they belong to, so it
    // hands those over rather than making somebody find the same rows again among a hundred. Both
    // kinds go: unanswered, and answered against a list this style does not use.
    const outstanding = [...h.FeedSizesWeCannotBuild, ...h.FeedSizesOnAnotherScale].map((s) => s.FeedSize);

    await DialogBaseComponent.open(SizeMappingDialogComponent, this, {
      manufacturerId: h.ManufacturerId,
      manufacturerName: h.ManufacturerName ?? '',
      focusFeatureId: h.HasSizeFeature ? h.SizeFeatureId : undefined,
      focusFeedSizes: outstanding,
      focusLabel: h.Style,
    });
    // Always, regardless of how the dialog closed: it records each answer as it is made rather
    // than on OK, so a cancel can still leave the panel out of date.
    await this.refreshFeedHealth();
  }

  /**
   * Change what one already-matched size means.
   *
   * The counterpart to onMatchSizes, and the only route to it. Match Sizes is built from sizes
   * with NO answer, so a hand answer that is wrong but valid - feed 2XL pointed at our 3X - is
   * unreachable there: it is answered, it builds products, and nothing downstream objects. This
   * panel is the only place that notices, because it is the only place that compares the mapping
   * against the size's own name, so it has to be the way back in.
   *
   * The size is named rather than the whole answered set reopened. Opening on a hundred settled
   * answers to change one would make the screen stop reading as a work queue.
   */
  async onChangeHandMatch(s: FeedSizeCoverageDTO) {
    const h = this.feedHealth;
    if (h == null || !h.HasSizeFeature) return;

    await DialogBaseComponent.open(SizeMappingDialogComponent, this, {
      manufacturerId: h.ManufacturerId,
      manufacturerName: h.ManufacturerName ?? '',
      focusFeatureId: h.SizeFeatureId,
      focusFeedSizes: [s.FeedSize],
      reopenFeedSizes: [s.FeedSize],
      focusLabel: h.Style,
    });
    await this.refreshFeedHealth();
  }

  /**
   * Whether the Product Builder may open.
   *
   * Deliberately separate from feed health. Health is the Commit bar; this asks whether the style
   * can be populated correctly right now. A style can read green and still be blocked here,
   * because a color we do not carry is a purchasing decision while a size the scale cannot express
   * is a gap in the vocabulary - the Builder would silently leave those products unbuilt.
   */
  get canOpenProductGenerator(): boolean {
    if (this.isAdding || !this.user.isProductAdmin) return false;

    // The Builder reads the SAVED style's pairs, while the panel describes the one on screen.
    // Building in between would make products for one style judged against another.
    if (this.isStyleUnsaved) return false;

    const h = this.feedHealth;
    // Before the feed escape below. A shape gap does not depend on the feed, and a style the feed
    // refused is exactly where one lives: Blue Generation's per-color styles would otherwise get
    // the manual pickers and build every product without a color.
    if (h?.FeatureShapeGaps?.length) return false;
    // No feed, or a style the feed does not answer for, restricts nothing.
    return h == null || !h.IsStyleStaged || h.CanBuildEverySize;
  }

  /** Why the Product Builder is closed, in one sentence. Null when it is open. */
  get productGeneratorBlockedReason(): string | null {
    // The real reason, before any feed verdict. canOpenProductGenerator already refuses while the
    // style is being added, so quoting a feed finding here named a fix that would not open it -
    // the operator matched the sizes and the Builder stayed shut, because saving was the gate.
    // if (this.isAdding) return 'Save the style to open the Product Builder.';

    if (!this.isAdding && this.isStyleUnsaved) return 'Save the style to open the Product Builder.';

    const h = this.feedHealth;
    // Same order as canOpenProductGenerator: the shape gap before the feed escape.
    if (h?.FeatureShapeGaps?.length) return h.FeatureShapeGaps[0].Message;
    if (h == null || !h.IsStyleStaged || h.CanBuildEverySize) return null;

    // Off rather than blocked: there is nothing to fix on a product type nobody sells.
    if (h.IsProductTypeInactive) return null;

    // The stop comes first. Nothing else about the style has been computed while it holds.
    if (h.DuplicateProductTypes.length > 0) {
      return `This style has ${h.DuplicateProductTypes.length} product types. It must have one.`;
    }

    if (!h.HasSizeFeature) return 'Attach a size feature to this style to check the sizes.';

    // Said separately because the two have different fixes. Match Sizes answers the first group
    // and cannot touch the second, which needs a product type retiring instead.
    if (h.FeedSizesOnAnotherScale.length > 0 && h.FeedSizesWeCannotBuild.length === 0) {
      const n = h.FeedSizesOnAnotherScale.length;
      return `${n} size${n === 1 ? ' is' : 's are'} matched to a different size list.`;
    }

    const n = h.FeedSizesWeCannotBuild.length;
    return `Match ${n} size${n === 1 ? '' : 's'} to build this style.`;
  }

  async onChangeFeatures() {
    if (!(await this.canChangeFeatures())) return;
    await DialogBaseComponent.open(ProductTypeChangeFeaturesDialogComponent, this, {
      productType: this.productType,
      // The dialog cannot tell an unsaved style from a saved one on its own - see the param.
      isAdding: this.isAdding,
    });
    await this.refreshProducts();
    // The size scale may have just become a different one, and the panel is answered against it.
    await this.refreshFeedHealth();
  }

  async onAddFeature() {
    if (!(await this.canAddFeature())) return;
    const tmpFeatures = await EntityFinderDialog.open(FeatureFinderDialogComponent, this, {
      excludedFeatureIds: this.productType.productTypeFeatureMaps.map((x) => x.featureId),
    });
    if (tmpFeatures.length == 0) return;
    const featureToAdd = tmpFeatures[0];

    if (featureToAdd.featureChoices.length == 0) {
      await this.dialogService.showOkMessage('Missing Feature Choices', 'The selected feature must have a least one feature choice');
      return;
    }

    this.allProducts = await this.dbQueryService.getProductsForProductType(this.productType.id);
    this.applyProductFilter();

    if (this.products.length == 0) {
      this.addFeature(featureToAdd);
    } else {
      const tmpFeatureChoices = await EntityFinderDialog.open(FeatureChoiceFinderDialogComponent, this, {
        featureId: featureToAdd.id,
        excludedFeatureChoiceIds: [],
      });
      if (tmpFeatureChoices.length == 0) {
        await this.dialogService.showOkMessage('Missing Feature Choice', 'You must select a feature choice');
        return;
      }
      const fcToAdd = tmpFeatureChoices[0];
      // Don't want to actually add the feature unless a featureChoice is also accepted.
      const ptfMap = this.addFeature(featureToAdd);

      // Need to add the selected featureChoice to each product with this featureChoice
      this.allProducts.forEach((p) => {
        const pfc = this.uow.createEntity(ProductFeatureChoice, {
          productId: p.id,
          featureChoiceId: fcToAdd.id,
        });
      });
      AgFns.refreshGrid(this.productGridOptions, this.products);
    }

    // The size question can only be answered once a size scale is attached, so the panel has to be
    // asked again here. Reached only when a feature was actually added - both refusals above return.
    await this.refreshFeedHealth();
  }

  /**
   * The feature brake. Add Feature gives the one choice it asks for to every product, so on a style
   * whose products were used it rewrites what every order, PO and item on them means (BG2201,
   * 2026-10-29). The server refuses the save; asking here first stops the operator before the work.
   */
  /**
   * The status guard. One stray click on the Status list set CT10NV - 15 job order lines to ship,
   * 10 PO lines, 5 Proximity cart lines - to Inactive on screen, one Save away from prod
   * (2026-10-30). Moving a saved style off Active while it has open work asks first, with the
   * counts. It refuses nothing: the Inactive gate (plan step L2) is the rule.
   *
   * Inactive gets its own, explicit warning (Jeff 2026-11-01): ten styles went Inactive with lines
   * still open at the supplier, so the operator must read what Inactive does and that Phaseout is
   * the status that lets the open work finish. Same advice as Edit Product's confirmInactive.
   */
  async onStatusChange() {
    if (this.isAdding || !(this.productType.id > 0)) return;
    const pt = this.productType;
    const saved = (pt.entityAspect.originalValues as any)['activeStatusId'] ?? pt.activeStatusId;
    const next = pt.activeStatusId;
    if (next === saved || next === ActiveStatusEnum.Active) return;

    const use = await this.productExposureApi.getForProductType(pt.id);
    const open = next === ActiveStatusEnum.Inactive ? ProductExposureFns.inFlightList(use) : ProductExposureFns.openWorkList(use);
    if (!open && use.ProximityChecked) return;

    const ok = next === ActiveStatusEnum.Inactive
      ? await this.confirmStyleInactive(open, use.ProximityChecked)
      : await this.confirmStyleStatus(next, open, use.ProximityChecked);
    if (ok) return;

    pt.activeStatusId = saved;
    // Nothing else changed: put the style back to unchanged, so the page does not ask to save.
    const changed = Object.keys(pt.entityAspect.originalValues ?? {});
    if (changed.every((k) => k === 'activeStatusId')) pt.entityAspect.rejectChanges();
  }

  private confirmStyleInactive(open: string, proximityChecked: boolean) {
    const message =
      (open ? `<p><b>This style has open work.</b> Every product of this style is in the counts:</p>${open}` : '') +
      (proximityChecked
        ? ''
        : '<p><b>The system cannot check Proximity.</b> Proximity can have orders on this style that are not in this list.</p>') +
      '<p><b>If you make the style Inactive, the open work cannot finish.</b></p>' +
      '<p><b>To let the open work finish, put the style on Phaseout.</b>' +
      ' Phaseout stops new orders and lets the open orders finish.' +
      ' When all the open work is done, make the style Inactive.</p>' +
      '<p>Do you want to make the style Inactive?</p>';
    return this.dialogService.yesNoDialog('Make the Style Inactive?', message, 'Make Inactive', 'Cancel');
  }

  private confirmStyleStatus(next: ActiveStatusEnum, open: string, proximityChecked: boolean) {
    const name = this.activeStatuses.find((x) => x.id === next)?.name ?? 'this status';
    const message =
      (open ? `This style has open work:${open}` : '') +
      (proximityChecked ? '' : '<p>The system cannot check Proximity.</p>') +
      `<p>Do you want to set the status to ${name}?</p>`;
    return this.dialogService.yesNoDialog('Change the Status?', message);
  }

  /**
   * Gatekeeper C2, Convert to Feed. Offered on a custom style whose name the feed now has (F4). One
   * save renames this style to K500-OLD and makes the new feed style K500 beside it, with this style's
   * features, description, costs, tags and alternate styles. The old products, orders and stock stay
   * on K500-OLD. The server decides that K500 is a feed style, as for any new style.
   *
   * Then, on K500: build the products with the Product Generator, map the old products (Map Old
   * Products), and put K500-OLD on Phaseout from the map.
   */
  async onConvertToFeed() {
    const old = this.productType;
    if (!this.user.isProductAdmin || !this.isCustomStyle || !this.feedHealth?.FeedHasThisStyle) return;
    if (!(await this.isPageSaved())) return;

    const title = 'Convert to Feed';
    const mfr = old.manufacturer;
    const style = (old.style ?? '').trim();
    const oldName = style + OLD_STYLE_SUFFIX;

    if (await this.dbQueryService.getProductTypeByStyle(old.manufacturerId, oldName)) {
      await this.dialogService.showOkMessage(
        title,
        `Style ${oldName} already exists for ${mfr.name}. The system cannot give this style that name. Open style ${oldName} and check it.`,
      );
      return;
    }

    const gap = await this.featureShapeGap(old.manufacturerId, this.attachedFeatureIds);
    if (gap) {
      await this.dialogService.showOkMessage(
        title,
        `A ${mfr.name} feed style needs ${gap.wanted}. This style has ${gap.found === 0 ? 'none' : gap.found}, so the system cannot copy its features.` +
          `<br/><br/>To convert it by hand:<br/>1. Change the SKU of this style to ${oldName}. Then save.` +
          `<br/>2. Add style ${style} with the features it needs.` +
          `<br/>3. Open style ${style} and select Map Old Products.`,
      );
      return;
    }

    // The new style is a feed style only if the feed answers for the name when it is saved.
    try {
      const run = await this.wrapBusy(() => this.stagedFeedApi.stageStyle(mfr.id, style));
      if (run.StylesFailed > 0) {
        await this.dialogService.showOkMessage(title, `The ${mfr.name} feed does not have style ${style} now. You cannot convert this style now. Try again later.`);
        return;
      }
    } catch {
      await this.dialogService.showOkMessage(title, `The system cannot reach ${mfr.name} now. You cannot convert this style now. Try again later.`);
      return;
    }

    const ok = await this.dialogService.yesNoDialog(
      'Convert to a Feed Style?',
      `The system makes a new feed style ${style}. It gets the features, description, costs, tags and alternate styles of this style.` +
        `<br/><br/>This style gets the name ${oldName}. Its products, orders and stock do not change.` +
        `<br/><br/>After this:<br/>1. Build the products of ${style} with the Product Generator.` +
        `<br/>2. Select Map Old Products. Map each product of ${oldName} to a product of ${style}.` +
        `<br/>3. Put ${oldName} on Phaseout.` +
        `<br/><br/>Do you want to convert this style?`,
      'Convert',
      'Cancel',
    );
    if (!ok) return;

    const tagMaps = await this.dbQueryService.getProductTypeTagMaps(old.id);
    const initials = this.authService.getUser().initials;
    const created = this.uow.createEntity(ProductType, {
      manufacturerId: old.manufacturerId,
      style: style,
      description: old.description,
      descriptionExt: old.descriptionExt,
      leadTimeDays: old.leadTimeDays,
      eOQTypeId: old.eOQTypeId,
      orderingCostAmt: old.orderingCostAmt,
      unitCarryingCostPerYearAmt: old.unitCarryingCostPerYearAmt,
      safetyStockPerYearPct: old.safetyStockPerYearPct,
      allowReorderFeatureChoices: old.allowReorderFeatureChoices,
      activeStatusId: ActiveStatusEnum.Active,
      modTs: new Date(),
      modUserInit: initials,
    });
    const children = [
      ...old.productTypeFeatureMaps.map((m) =>
        this.uow.createEntity(ProductTypeFeatureMap, { productTypeId: created.id, featureId: m.featureId, displayOrder: m.displayOrder }),
      ),
      ...tagMaps.map((t) => this.uow.createEntity(ProductTypeTagMap, { productTypeId: created.id, productTypeTagId: t.productTypeTagId })),
      ...(old.productTypeAltProductTypes ?? []).map((a) =>
        this.uow.createEntity(ProductTypeAlt, { productTypeId: created.id, altProductTypeId: a.altProductTypeId, fitness: a.fitness }),
      ),
    ];
    old.style = oldName;

    await this.wrapBusy(() => this.dbSaveService.saveOwnChanges([old, created, ...children]));

    if (!created.isFeedEnabled) {
      await this.dialogService.showOkMessage(
        title,
        `The feed did not answer for style ${style} when the system saved it, so ${style} is a custom style. Tell a system administrator.`,
      );
    }
    // Read the style once more, so the staged feed row points at the new style, not the renamed one.
    try {
      await this.stagedFeedApi.stageStyle(mfr.id, style);
    } catch {
      // The next feed sweep links it.
    }
    this.toastr.success(`The system made the feed style ${style}. The old style is now ${oldName}.`);
    this.router.navigate(['/references/manage/product-type', created.id]);
  }

  /**
   * Gatekeeper C2: the other style of a conversion. On K500-OLD it is K500; on K500 it is K500-OLD.
   * Found by name, so a conversion made by hand shows here too. Only a feed style replaces an old one.
   */
  conversionPartner: ProductType | null = null;
  /** This page is the old style of the conversion. */
  isConversionOld = false;
  conversionOldProductCount = 0;
  conversionConfirmedCount = 0;

  private async refreshConversion() {
    this.conversionPartner = null;
    const pt = this.productType;
    if (this.isAdding || !(pt?.id > 0)) return;
    const style = (pt.style ?? '').trim();
    const isOld = style.toUpperCase().endsWith(OLD_STYLE_SUFFIX);
    const partnerStyle = isOld ? style.slice(0, -OLD_STYLE_SUFFIX.length) : style + OLD_STYLE_SUFFIX;
    if (!partnerStyle) return;
    const partner = await this.dbQueryService.getProductTypeByStyle(pt.manufacturerId, partnerStyle);
    if (partner == null || partner.id === pt.id) return;
    const oldStyle = isOld ? pt : partner;
    const newStyle = isOld ? partner : pt;
    if (!newStyle.isFeedEnabled) return;

    const rows = await this.dbQueryService.getProductReplacementsForOldStyle(oldStyle.id);
    const oldProducts = isOld ? this.allProducts : await this.dbQueryService.getProductsForProductType(oldStyle.id);
    this.conversionOldProductCount = oldProducts.length;
    this.conversionConfirmedCount = rows.filter((r) => r.confirmedTs != null).length;
    this.isConversionOld = isOld;
    this.conversionPartner = partner;
  }

  async onMapOldProducts() {
    const partner = this.conversionPartner;
    if (partner == null || !this.user.isProductAdmin) return;
    if (!(await this.isPageSaved())) return;
    const saved = await DialogBaseComponent.open(ProductReplacementDialogComponent, this, {
      oldProductTypeId: this.isConversionOld ? this.productType.id : partner.id,
      newProductTypeId: this.isConversionOld ? partner.id : this.productType.id,
      dialogWidth: '900px',
      dialogHeight: '80vh',
    });
    if (saved) {
      await this.refreshConversion();
      this.exposurePanel?.refresh();
    }
  }

  openConversionPartner() {
    if (this.conversionPartner) this.router.navigate(['/references/manage/product-type', this.conversionPartner.id]);
  }

  /**
   * The dialogs this page opens share its entity manager, and they save and undo all of it: OK
   * would send the page's unjudged edits with the dialog's, and Cancel would throw them away
   * (feed audit E4). So they open only on a saved page. Change Features saves only its own
   * entities and must work on a new style, so it does not ask. Match Sizes asks inside its dialog.
   */
  private async isPageSaved() {
    if (!this.uow.hasChanges()) return true;
    await this.dialogService.showOkMessage(
      'Save the Page First',
      'This page has changes that are not saved. Save or undo the changes. Then try again.',
    );
    return false;
  }

  private async canAddFeature() {
    if (this.isAdding || !(this.productType.id > 0)) return true;
    const use = await this.productExposureApi.getForProductType(this.productType.id);
    if (use.ProductCount === 0) return true;
    if (use.UsedProducts > 0) {
      await this.dialogService.showOkMessage(
        'You Cannot Add a Feature',
        'This style has products that were used on orders. You cannot add or remove a feature. Make a new style with the features you need.',
      );
      return false;
    }
    if (use.UnknownProducts > 0) {
      await this.dialogService.showOkMessage(
        'You Cannot Add a Feature',
        'The system cannot check Proximity, so it cannot tell if the products of this style were used. You cannot add or remove a feature now. Try again later.',
      );
      return false;
    }
    return true;
  }

  /**
   * Gatekeeper P3.4. Change Features takes a feature off every product of the style and gives each
   * one a choice of another, so on a style whose products were used it changes what every order,
   * PO and item on them means. The server judges the save under the gate mode. This asks first only
   * when the server enforces, so the operator is stopped before mapping the choices.
   */
  private async canChangeFeatures() {
    if (this.isAdding || !(this.productType.id > 0)) return true;
    const use = await this.productExposureApi.getForProductType(this.productType.id);
    if (use.ProductCount === 0 || !use.MeaningGateEnforced) return true;
    if (use.UsedProducts > 0) {
      await this.dialogService.showOkMessage(
        'You Cannot Change a Feature',
        'This style has products that were used on orders. You cannot change its features. Make a new style with the features you need.',
      );
      return false;
    }
    if (use.UnknownProducts > 0) {
      await this.dialogService.showOkMessage(
        'You Cannot Change a Feature',
        'The system cannot check Proximity, so it cannot tell if the products of this style were used. You cannot change a feature now. Try again later.',
      );
      return false;
    }
    return true;
  }

  private addFeature(featureToAdd: Feature) {
    var displayOrder = 1;
    if (this.productType.productTypeFeatureMaps.length > 0) {
      displayOrder = 1 + (_.max(this.productType.productTypeFeatureMaps.map((x) => x.displayOrder)) ?? 0);
    }
    return this.uow.createEntity(ProductTypeFeatureMap, {
      productTypeId: this.productType.id,
      featureId: featureToAdd.id,
      displayOrder: displayOrder,
    });
  }

  viewFeature(feature: Feature) {
    // TODO; we really want to navigate to a specific feature.
    this.router.navigate(['/references/manage/features']);
    return;
  }

  onAllowReorderFeatureChange() {
    AgFns.refreshGrid(this.productGridOptions, this.products);
  }

  async selectManufacturer() {
    const r = await EntityFinderDialog.open(ManufacturerFinderDialogComponent, this);
    const manufacturer = _.first(r);
    if (manufacturer == null) return;
    this.productType.manufacturer = manufacturer;

    // The answer is per manufacturer, so a SKU checked against the previous one is not checked
    // any more. Re-ask when a SKU is already typed; otherwise this just unlocks the form.
    this.clearStyleCheck();
    await this.onStyleBlur();

    // The SKU is the only thing left to do once a manufacturer is chosen, so go there.
    await UtilFns.wait(1);
    this.firstInput?.nativeElement?.focus();
  }

  // searchManufacturer(query: string) {
  //   this.searchManufs = this.allManufs.filter(m => m.name.toLowerCase().indexOf(query.toLowerCase()) > -1);
  // }

  async onProductGridReady(event: GridReadyEvent) {
    const colDefs = [
      UniAgFns.getProductSortOrderColDef((x) => x.data, 'productSortOrder', false),
      {
        headerName: 'Value',
        field: 'featureChoicesExtract',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(150),
      },
      {
        headerName: 'Reorder Value',
        filter: 'agTextColumnFilter',
        valueGetter: (params) => {
          const prod = <Product>params.data;
          var r = prod && prod.getReorderProduct().getFeatureChoicesExtract();
          if (r == prod.featureChoicesExtract) {
            return '[same]';
          } else {
            return r;
          }
        },
        ...AgFns.flexWidth(150),
      },
      { ...UniAgFns.getActiveStatusColDef('activeStatus.name'), ...AgFns.fixWidth(100) },
      // { headerName: "Id", field: "id", filter: "agNumberColumnFilter" },
      // { headerName: 'Product Type Id', field: 'productTypeId', filter: 'agNumberColumnFilter' },
    ];
    if (this.user.isProductAdmin) {
      const toggleStatusColDef = {
        ...AgFns.createButtonProps('', this.onToggleProductStatus.bind(this), {
          label: 'Toggle Status',
        }),
        colId: 'toggleStatus',
        ...AgFns.fixWidth(130),
      };
      // Insert immediately before the 'Status' column (currently colDefs[3]).
      colDefs.splice(3, 0, toggleStatusColDef as any);

      const invColDefs = [
        {
          ...AgFns.createButtonProps('', this.onCombine.bind(this), {
            label: 'Combine',
            canDisplay: this.canCombine.bind(this),
          }),
          colId: 'combine',
          ...AgFns.fixWidth(100),
        },
        {
          ...AgFns.createButtonProps('', this.editProduct.bind(this), {
            label: 'Edit Product',
          }),
          ...AgFns.fixWidth(120),
        },
      ];
      // After the toggleStatus insert above, the trailing admin buttons should sit after Status,
      // which is now at index 4. So splice at 5 to land them after Status.
      colDefs.splice(5, 0, ...(invColDefs as any[]));
    }
    const sortModel = [{ colId: 'productSortOrder', sort: 'asc' }] as ISortModel;
    AgFns.initGrid(event, colDefs, sortModel);
    this.onAllowCombinationChange();
  }

  async onAltItsGridReady(event: GridReadyEvent) {
    const colDefs = [
      { ...UniAgFns.getManufacturerColDef('manufacturer.name') },
      { ...UniAgFns.getProductTypeSKUColDef('style') },
      { ...UniAgFns.getProductTypeDescriptionColDef('description') },

      // { headerName: "Id", field: "id", filter: "agNumberColumnFilter" },
    ];
    if (this.user.isProductAdmin) {
      const invColDefs = [
        {
          ...AgFns.createButtonProps('', this.removeAltProductType.bind(this), {
            label: 'Remove Alternate SKU',
          }),
          minWidth: 180,
        },
      ];
      colDefs.splice(3, 0, ...(invColDefs as any[]));
    }
    const sortModel = [
      { colId: 'manufacturer.name', sort: 'asc' },
      { colId: 'description', sort: 'asc' },
      { colId: 'style', sort: 'asc' },
    ] as ISortModel;
    AgFns.initGrid(event, colDefs, sortModel);
  }

  async onTabChanged(evt: any) {
    const newIndex = evt.index;
    // const currentTabLabel = evt.tab.textLabel;
    // HACK: needed because AgGrid loses buttons when tabbing from one tab to another and then back.
    this.products = this.products.slice();
    // this.altProductTypes = this.altProductTypes.slice();
    AgFns.refreshGrid(this.altItsGridOptions, this.altProductTypes);
  }

  // --------------------------------------------------------------------

  async addProductTypeCopy() {
    this.isAdding = true;
    const oldProductType = this.productType;
    this.productType = <ProductType>EntityFns.cloneStruct(oldProductType, ['id', 'style', 'description', 'descriptionExt']);
    this.searchManufs = [oldProductType.manufacturer];
  }

  async onProductRowSelected(event: RowSelectedEvent) {
    if (!event.node.isSelected()) {
      return;
    }
    const prod = event.data as Product;
    if (prod == null) {
      return;
    }

    this.selectedProduct = prod;
  }

  onAllowCombinationChange() {
    this.productGridOptions.apiX?.setColumnsVisible(['combine', 'toggleStatus'], this.allowCombination);
    this.productGridOptions.apiX?.setColumnWidths([
      { key: 'combine', newWidth: 115 },
      { key: 'toggleStatus', newWidth: 130 },
    ]);
    this.rowSelection = this.allowCombination ? RowSelectionMultipleCheckbox : RowSelectionSingle;
    AgFns.sizeColumnsToFit(this.productGridOptions);
  }

  async onToggleProductStatus(product: Product, event?: CellClickedEvent, origEvent?: UIEvent) {
    origEvent?.stopPropagation();
    if (product == null) return;

    if (this.uow.hasChanges()) {
      await this.dialogService.showOkMessage(
        'Cannot toggle status',
        'You have unsaved changes. Save or cancel them before toggling product status.',
      );
      return;
    }

    product.activeStatusId = UniAgFns.nextToggleStatus(product.activeStatusId);

    const ok = await this.dbSaveService.saveChanges();
    if (!ok) return;

    this.productGridOptions.apiX?.refreshCells({ force: true });

    this.toastr.success(`The product status is now ${ActiveStatusEnum[product.activeStatusId]}.`);

    // Retiring the last product in a color removes that color from the style, so the health
    // panel's answer changes with this toggle.
    await this.refreshFeedHealth();
    this.exposurePanel?.refresh();
  }

  canCombine(combineIss: Product) {
    if (this.productGridOptions.apiX == null) {
      return false;
    }

    if (this.isFeedLocked) return false;
    return combineIss?.id == this.selectedProduct?.id && this.productGridOptions.apiX.getSelectedRows().length > 1;
  }

  async onCombine(toProduct: Product, event?: CellClickedEvent, origEvent?: UIEvent) {
    // Needed to stop onRowSelected call from being made right after this call OR during any await call within this method.
    // The askIfOk call below is async and the OnRowSelected will get called during its execution which will in turn
    // deselect all of the rows except the current one.
    origEvent?.stopPropagation();

    if (toProduct == null) {
      return;
    }
    const fromProducts = (this.productGridOptions.apiX?.getSelectedRows() as Product[]).filter((x) => x != toProduct);
    if (fromProducts.length < 1) {
      return;
    }

    const ok = await this.dialogService.yesNoDialog(
      'Combine Products',
      `Are you sure that you want to combine these ${1 + fromProducts.length} sizes?`,
    );
    if (!ok) return;
    await AgFns.busyGrid(this.productGridOptions, this, async () => {
      const fromProductIds = fromProducts.map((x) => x.id);
      const count = await this.dbSaveService.combineProducts(fromProductIds, toProduct.id);
      this.toastr.success(`${count} record(s) updated as a result of combining these products`);
    });

    await this.refreshProducts();
  }

  // -------------------------------------------------------------------------------

  async addProducts() {
    if (!this.productType?.id) return;
    // The button is already disabled for this. Repeated here so the two cannot disagree - a
    // disabled button is manners, not a guarantee.
    if (!this.canOpenProductGenerator) return;
    if (!(await this.isPageSaved())) return;

    const r = await DialogBaseComponent.open(ProductBuilderDialogComponent, this, {
      productType: this.productType,
    });
    if (r) {
      await this.refreshProducts();
    }
  }

  async editProduct(iss: Product) {
    if (!(await this.isPageSaved())) return false;
    const r = await DialogBaseComponent.open(EditProductDialogComponent, this, {
      product: iss,
    });
    if (r) {
      await this.refreshProducts();
    }
    return r;
  }

  private async refreshProducts() {
    this.allProducts = await this.dbQueryService.getProductsForProductType(this.productType.id);
    this.applyProductFilter();
    AgFns.refreshGrid(this.productGridOptions, this.products);
    // Combine moves orders from one product to another, so the counts change with it.
    this.exposurePanel?.refresh();
  }

  applyProductFilter() {
    this.products = this.shouldShowInactiveProducts
      ? this.allProducts
      : this.allProducts.filter((p) => p.activeStatusId !== ActiveStatusEnum.Inactive);
  }

  onShouldShowInactiveProductsChange() {
    this.applyProductFilter();
    AgFns.refreshGrid(this.productGridOptions, this.products);
  }

  // -----------------------------------------------------------------------------

  async getAltProductTypes(its: ProductType) {
    this.altProductTypes = await AgFns.busyGrid(this.altItsGridOptions, this, () => {
      return this.dbQueryService.getAltProductTypesForProductType(its.id);
    });
    return this.altProductTypes;
  }

  async addAltProductType() {
    const altPts = await EntityFinderDialog.open(ProductTypeFinderDialogComponent, this, {
      header: 'Select Alternate Product Types',
      rowSelection: RowSelectionMultipleCheckbox,
    });
    if (altPts.length == 0) return;
    const added: ProductTypeAlt[] = [];
    altPts.forEach((altPt) => {
      const alreadyExists = this.productType.productTypeAlts.some((ita) => ita.altProductTypeId === altPt.id);
      if (!alreadyExists) {
        added.push(this.createAltProductType(altPt));
      }
    });

    await this.dbSaveService.saveOwnChanges(added);
    this.toastr.success(`${added.length} alternative style(s) added`);

    this.altProductTypes = await this.getAltProductTypes(this.productType);
  }

  createAltProductType(altPt: ProductType) {
    const struct = <ProductTypeAlt>{};
    struct.productTypeId = this.productType.id;
    struct.altProductTypeId = altPt.id;
    struct.fitness = 0;
    // struct.crtnTs = new Date();
    // struct.crtnUserInit = this.authService.getUser().userInit,
    return this.uow.createEntity(ProductTypeAlt, struct);
  }

  async removeAltProductType(altIts: ProductType) {
    const isaToRemove = this.productType.productTypeAlts.find((isa) => isa.altProductTypeId === altIts.id);
    isaToRemove.entityAspect.setDeleted();
    await this.dbSaveService.saveOwnChanges([isaToRemove]);
    this.altProductTypes = await this.getAltProductTypes(this.productType);
    this.toastr.success('Alternative Style removed.');
  }

  // --------------------------------------------------------------------------------

  async saveChanges() {
    if (this.productType.productTypeFeatureMaps.length == 0) {
      await this.dialogService.showOkMessage('Cannot save', 'You must have a least one feature for this product type');
      return false;
    }

    const shapeError = await this.findFeatureShapeError();
    if (shapeError) {
      await this.dialogService.showOkMessage('The Features Are Not Right Yet', shapeError);
      return false;
    }

    // Trim first. A trailing space is the difference between a duplicate the database sees and
    // one a person sees, so the checks below have to run against the trimmed value.
    this.productType.style = this.productType.style?.trim();

    // Renaming a style into one that already exists makes the same duplicate as creating it,
    // and this used to run on add only. Changing the manufacturer does the same thing.
    const original = this.productType.entityAspect.originalValues ?? {};
    const styleMoved = this.isAdding || 'style' in original || 'manufacturerId' in original;

    if (styleMoved) {
      const dup = await this.findDuplicateProductType();
      if (dup) {
        await this.dialogService.showOkMessage(
          'Duplicate Product Type',
          `A Product Type with Manufacturer "${dup.manufacturer?.name}" and SKU "${dup.style}" already exists ` +
            `(Description: "${dup.description}"). Please pick a different SKU, or edit the existing Product Type instead.`,
        );
        return false;
      }

      if (!(await this.isStyleInTheFeed())) return false;
    }

    if (this.isAdding) {
      this.productType.entityAspect.setAdded();
    }

    const ea = this.productType.entityAspect;
    if (!ea.validateEntity()) {
      const vErrors = ea.getValidationErrors();
      const errMsg = vErrors.map((ve) => ve.errorMessage).join('<br>');
      await this.dialogService.showOkMessage('Cannot save - Validation Errors', errMsg);
      return false;
    }
    this.productType.modTs = new Date(Date.now());
    await this.imageHandler?.attachFiles();

    // false: a refused save leaves the page as the operator typed it. A rollback would undo every
    // edit, and on Add it detaches the new style from the page (feed audit D4).
    const sr = await this.dbSaveService.saveChanges(false);

    this.toastr.success('Product Type updated');
    this.isAdding = false;
    // Health is computed on the server from products, colors and descriptions - all of which
    // this save may have just changed. Recompute rather than leave the panel stating the
    // position before the save.
    await this.refreshFeedHealth();
    await this.refreshConversion();
    this.exposurePanel?.refresh();

    // A gap that survives a save got through the exemption in findFeatureShapeError: an edit that
    // touches no features is not judged. Allowed on purpose, but said out loud rather than left
    // for the operator to find in the panel.
    const gap = this.feedHealth?.FeatureShapeGaps?.[0];
    if (gap) this.toastr.warning(gap.Message, 'The Features Are Not Right Yet');
    return true;
  }

  /**
   * Ask the manufacturer whether it publishes this style, before the style is created.
   *
   * This is the gap LK864 came through. A style was typed that SanMar has never published; it
   * reached Proximity's master catalog, and every sweep afterwards asked SanMar about a style
   * that does not exist. Nothing anywhere said no, because the Committed gate that would have
   * refused it only runs once a manufacturer IS Committed - which it cannot become while styles
   * like that are in the catalogue.
   *
   * Deliberately here and not in the save interceptor. Reading a style is a network call to the
   * manufacturer, and the intake design forbids one in the Breeze save path - that is the whole
   * reason the staged tables exist. Here a person is already waiting for a save, one call is
   * nothing, and the style is staged as a side effect so the next sweep has nothing to discover.
   *
   * A feed the system cannot reach is NOT a refusal. An outage at the manufacturer must not stop
   * someone building the catalogue, so that case asks rather than blocks.
   */
  private async isStyleInTheFeed(): Promise<boolean> {
    const mfr = this.productType.manufacturer;
    const style = this.productType.style;
    if (!style || mfr == null) return true;
    if (mfr.manufacturerFeedStatusId === ManufacturerFeedStatusEnum.None) return true;
    if (this.isCustomStyle) return true;
    if (this.isOldStyleRename) return true;

    // Leaving the SKU field already asked, and the answer cannot have changed since. Asking a
    // second time would make every save of an already checked style wait on the manufacturer.
    if (this.styleKey(mfr.id, style.trim()) === this.checkedStyleKey) return true;

    try {
      const run = await this.stagedFeedApi.stageStyle(mfr.id, style);
      if (run.StylesFailed === 0) return true;
      if (this.isAdding) return await this.confirmCustomStyle(mfr.name, style, this.isStyleRefusal(run.Error));

      await this.dialogService.showOkMessage(
        'The Manufacturer Does Not Make This Style',
        `${mfr.name} does not publish style '${style}'.` +
          (run.Error ? `<br/><br/>${run.Error}` : '') +
          `<br/><br/>Check the style against the manufacturer's catalog, then enter it again.`,
      );
      return false;
    } catch {
      if (this.isAdding) return await this.confirmCustomStyle(mfr.name, style, false);
      return await this.dialogService.yesNoDialog(
        'The System Cannot Reach the Manufacturer',
        `The system could not ask ${mfr.name} about style '${style}', so the style is not checked.` +
          `<br/><br/>Do you want to save it anyway?`,
        'Ok',
        'Cancel',
      );
    }
  }

  /**
   * A new style the feed does not answer for is saved as a custom style (gatekeeper F2). The server
   * decides IsFeedEnabled when the style is created and the answer never changes, so the operator
   * is told before the save, not after. When the feed cannot be reached, cancelling and trying
   * later is the way to get a feed style.
   */
  private async confirmCustomStyle(mfrName: string, style: string, isNotInFeed: boolean) {
    const reason = isNotInFeed
      ? `The ${mfrName} feed does not have style '${style}'.`
      : `The system cannot reach ${mfrName}, so it cannot check style '${style}'.` +
        ` To make a feed style, select Cancel and try again later.`;
    return await this.dialogService.yesNoDialog(
      'Save as a Custom Style?',
      `${reason}<br/><br/>If you save, the system saves it as a custom style. A custom style does not use the feed.` +
        ` You cannot change it to a feed style later.<br/><br/>Do you want to save it as a custom style?`,
      'Ok',
      'Cancel',
    );
  }

  /**
   * Whether this style carries exactly the feature kinds its manufacturer requires.
   *
   * The same rule the save interceptor enforces, said here so it fails on the screen rather than
   * as a server exception after a round trip. The server stays authoritative - this is the
   * manners, not the guarantee.
   *
   * Names no kind itself. It compares the kinds required against the kinds present and takes
   * the wording from FeatureKind.name, so a kind added later needs no change here.
   */
  private async findFeatureShapeError(): Promise<string | null> {
    const manufacturerId = this.productType.manufacturerId;
    if (manufacturerId == null) return null;

    // An edit that touches none of this style's features cannot change its shape, so there is
    // nothing to judge - the server's ValidateProductTypeFeatureShape skips the same case. Without
    // this the screen refused what the server allows: setting a Blue Generation per-color style
    // (Size, no Color) to Inactive asked for a Color feature first, on a style being retired.
    // getChanges includes deleted maps, as the server's save bundle does.
    const featuresTouched = this.uow.getChanges()
      .some((e) => e instanceof ProductTypeFeatureMap && e.productTypeId === this.productType.id);
    if (!this.isAdding && !featuresTouched) return null;

    const gap = await this.featureShapeGap(manufacturerId, this.attachedFeatureIds);
    if (gap == null) return null;
    return gap.found < gap.want
      ? `This style needs ${gap.wanted}, and it has ${gap.found === 0 ? 'none' : gap.found}.` + ` Add a ${gap.kind} feature before you save.`
      : `This style needs ${gap.wanted}, and it has ${gap.found}.` + ` Remove the ones you do not want.`;
  }

  /**
   * The first feature kind the manufacturer requires that these features do not have the right
   * number of. Mirrors ValidateProductTypeFeatureShape on the server, including that requiredCount
   * is exact. Too many is as wrong as too few - the feed matcher has to know which feature answers
   * which question, and a spare one makes that ambiguous.
   */
  private async featureShapeGap(manufacturerId: number, featureIds: number[]) {
    const required = await this.dbQueryService.getRequiredFeatureKinds(manufacturerId);
    const kindByFeatureId = new Map(this.features.map((f) => [f.id, f.featureKindId]));
    const kinds = featureIds.map((id) => kindByFeatureId.get(id));
    for (const req of required) {
      const want = Math.max(1, req.requiredCount ?? 1);
      const found = kinds.filter((k) => k === req.featureKindId).length;
      if (found === want) continue;
      const kind = req.featureKind?.name ?? 'required';
      return { kind, want, found, wanted: want === 1 ? `one ${kind} feature` : `${want} ${kind} features` };
    }
    return null;
  }

  private async findDuplicateProductType(): Promise<ProductType | undefined> {
    const manufacturerId = this.productType.manufacturerId;
    const style = this.productType.style?.trim();
    if (!manufacturerId || !style) return undefined;
    const existing = await this.dbQueryService.getProductTypesForManuf(manufacturerId);
    return existing.find((pt) => pt.id !== this.productType.id && (pt.style ?? '').trim().toLowerCase() === style.toLowerCase());
  }

  /** Create the join entity between Image and parent entity */
  createImageJoinEntity(event: { imageId: number; isPrimary: boolean }): void {
    this.dbSaveService.uow.createEntity(ProductTypeImage, {
      productTypeId: this.productType.id,
      imageId: event.imageId,
      isPrimary: event.isPrimary,
    });
  }

  // async changeFeature(feature: Feature) {
  //   if (this.productType.productTypeFeatureMaps.length >1) {
  //     await this.dialogWrapperService.showOkMessage("Migration Operation not allowed", "Multi feature product types do not support this operation");
  //     return;
  //   }
  //   const migrationType = _MigrationProductTypeChange.getMigrationType(this.productType, this.migrationProductTypeChange);
  //   if (migrationType == MigrationType.Reverting) {
  //     await this.dialogWrapperService.showOkMessage('Ths migration is marked for reversion', `This change from Feature: '${this.migrationProductTypeChange.fromOrigFeatureName}'
  //       to '${this.migrationProductTypeChange.toOrigFeatureName}' has been marked for reversion.  No further mapping can be completed
  //       until after the next migration.`);
  //     return;
  //   }
  //   if (migrationType != MigrationType.Pending && migrationType != MigrationType.None) {
  //     const ok = await this.dialogWrapperService.askYesNo(`Revert?`, `This change from Feature: '${this.migrationProductTypeChange.fromOrigFeatureName}'
  //      to '${this.migrationProductTypeChange.toOrigFeatureName}' has already been completed in a previous migration.
  //      Do you want to revert it? ( this will occur during the next migration)`);
  //     if (ok) return;

  //     this.migrationProductTypeChange.toOrigFeatureName = 'XXX-' + this.migrationProductTypeChange.toOrigFeatureName;
  //     await this.dbSaveService.saveSelectedChanges([this.migrationProductTypeChange]);
  //     this.migrationProductTypeChange = await this.dbQueryService.getMigrationProductTypeChange(this.productType.id);
  //     return;
  //   }

  //   const x = await DialogBaseComponent.open(ProductTypeFeatureChangeDialogComponent, this,   {
  //       productTypeId: this.productType.id,
  //   });
  //   this.migrationProductTypeChange = await this.dbQueryService.getMigrationProductTypeChange(this.productType.id);
  // }

  // get migrationPendingClassName() {

  //   const migrationType = _MigrationProductTypeChange.getMigrationType(this.productType, this.migrationProductTypeChange);

  //   if (migrationType == MigrationType.Pending) {
  //     return 'accent-yellow' ;
  //   } else if (migrationType == MigrationType.Complete) {
  //     return 'accent-green'
  //   } else if (migrationType == MigrationType.Reverting) {
  //     return 'accent-red';
  //   } else {
  //     return 'accent-blue';
  //   }
  // }
}
